import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/onboarding.css";
import "../../../assets/css/stepper.css";
import { toast } from "sonner";
import {
  callToast,
  prepareSiteList,
  showErrorMessages,
} from "../../../../src/services/utilityFunctions.js";
import { TimePicker } from "antd";
import { getLocalStorageData } from "../../../../src/services/axiosComponent";
import { useGlobalState } from "../../../../src/services/hostelConst.js";
import Select from "react-select";
import { selectStyle } from "../../../services/constant";
import { closeModal } from "../../../services/redux/reducers/modalSlice";
import { useSelector, useDispatch } from "react-redux";
import CustomTimePicker from "./DurationSelector.js";
import {
  addExaminationTemplate,
  updateExaminationTemplate,
  getExaminationTemplateDetails,
} from "../../../services/Users-services.js";
import {
  getAcademicYearList,
  getDivisionList,
  getDivisionSectionList,
  getCoursesList,
  getDivisionSectionSubjectList,
} from "../../../services/Academic-Services.js";
import { duration } from "@mui/material";

const AddExaminationTemplateForm = (props) => {
  const { refresh, navigate } = props;
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const modal = useSelector((state) => state.modal);

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(modal.isEditing);

  // Options state
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);

  // Loading states
  const [isAcademicYearLoading, setIsAcademicYearLoading] = useState(false);
  const [isSiteOptionsLoading, setIsSiteOptionsLoading] = useState(false);
  const [isDivisionLoading, setIsDivisionLoading] = useState(false);
  const [isSectionLoading, setIsSectionLoading] = useState(false);
  const [isCourseLoading, setIsCourseLoading] = useState(false);
  const [isSubjectLoading, setIsSubjectLoading] = useState(false);

  // Searchable states
  const [isSiteSearchable, setIsSiteSearchable] = useState(true);
  const [isRegionSearchable, setIsRegionSearchable] = useState(true);

  const examTypeOptions = [
    { value: "SUBJECTIVE", label: "Subjective" },
    { value: "OBJECTIVE", label: "Objective" },
    { value: "MIXED", label: "Mixed" },
  ];

  const prepareFormData = (data = null) => {
    return {
      examInfo: {
        name: data?.name || "",
        description: data?.description || "",
        academicYear: {
          value: data?.academicYearId || "",
          label: data?.academicYearName || "",
        },
        examType: {
          value: data?.examType || "",
          label: data?.examType || "",
        },
        score: data?.score || "",
        duration: data?.duration || "",
      },
      academicInfo: {
        region: {
          value: data?.regionId || "",
          label: data?.regionName || "",
        },
        site: {
          value: data?.siteId || "",
          label: data?.siteName || "",
        },
        division: {
          value: data?.divisionId || "",
          label: data?.divisionName || "",
        },
        section: {
          value: data?.sectionId || "",
          label: data?.sectionName || "",
        },
        course: {
          value: data?.courseId || "",
          label: data?.courseName || "",
        },
        subject: {
          value: data?.subjectId || "",
          label: data?.subjectName || "",
        },
      },
    };
  };

  const [formData, setFormData] = useState(prepareFormData());

  const getExamDetailsData = async () => {
    setLoadingData(true);
    try {
      var res = await getExaminationTemplateDetails(modal.data._ID);
      if (res.status === "success") {
        setData(res.data);
        setFormData(prepareFormData(res.data));
      } else {
        dispatch(closeModal());
        showErrorMessages({ data: res, navigate });
      }
    } catch (error) {
      console.error("Error fetching exam details:", error);
      showErrorMessages({
        data: { message: "Failed to fetch exam details" },
        navigate,
      });
    }
    setLoadingData(false);
  };

  const handleChange = (step, field, value) => {
    setErrors({});

    if (step === "academicInfo") {
      if (field === "region") {
        setFormData((prev) => ({
          ...prev,
          academicInfo: {
            ...prev.academicInfo,
            region: value,
            site: { value: "", label: "" },
            division: { value: "", label: "" },
            section: { value: "", label: "" },
            course: { value: "", label: "" },
            subject: { value: "", label: "" },
          },
        }));
        if (value?.value) {
          getSiteList(value.value);
        }
        return;
      }

      if (field === "site") {
        setFormData((prev) => ({
          ...prev,
          academicInfo: {
            ...prev.academicInfo,
            site: value,
            division: { value: "", label: "" },
            section: { value: "", label: "" },
            course: { value: "", label: "" },
            subject: { value: "", label: "" },
          },
        }));
        if (value?.value) {
          getDivisionOptions(formData.academicInfo.region.value, value.value);
        }
        return;
      }

      if (field === "division") {
        setFormData((prev) => ({
          ...prev,
          academicInfo: {
            ...prev.academicInfo,
            division: value,
            section: { value: "", label: "" },
            course: { value: "", label: "" },
            subject: { value: "", label: "" },
          },
        }));
        if (value?.value) {
          getSectionOptions(
            value.value,
            formData.academicInfo.region.value,
            formData.academicInfo.site.value
          );
        }
        setCourseOptions([]);
        setSubjectOptions([]);
        return;
      }

      if (field === "section") {
        setFormData((prev) => ({
          ...prev,
          academicInfo: {
            ...prev.academicInfo,
            section: value,
            subject: { value: "", label: "" },
          },
        }));

        if (value?.value) {
          getSubjectOptions(value.value);
        }
        setSubjectOptions([]);
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [step]: { ...prev[step], [field]: value },
    }));
  };

  // API calls for fetching options
  const getAcademicYearOptions = async () => {
    setIsAcademicYearLoading(true);
    try {
      var res = await getAcademicYearList();
      if (res.status === "success") {
        const list = res.data.entityList.map((item) => ({
          value: item._ID,
          label: item.NAME,
        }));
        setAcademicYearOptions(list);
      } else {
        showErrorMessages({ data: res, navigate });
      }
    } catch (error) {
      console.error("Error fetching academic years:", error);
      showErrorMessages({
        data: { message: "Failed to fetch academic years" },
        navigate,
      });
    }
    setIsAcademicYearLoading(false);
  };

  const getRegionList = async () => {
    var data = await JSON.parse(localStorage.getItem("profileDetails"));
    var list = JSON.parse(localStorage.getItem("regionList"));
    if (
      list.length == 1 &&
      list[0].value == data._REGION &&
      data["_REGION_ACCESS_"] == null
    ) {
      setIsRegionSearchable(false);
      handleChange("academicInfo", "region", {
        value: list[0].value,
        label: list[0].label,
      });
    }
    setRegionOptions(list);
  };

  const getSiteList = async (regionId, query) => {
    setIsSiteOptionsLoading(true);
    var data = await JSON.parse(localStorage.getItem("profileDetails"));
    var list = await prepareSiteList(regionId, navigate, query);

    if (
      list.length == 1 &&
      list[0].value == data._SITE &&
      data["_SITE ACCESS_"] == null
    ) {
      setIsSiteSearchable(false);
      handleChange("academicInfo", "site", {
        value: list[0].value,
        label: list[0].label,
      });
    }

    setIsSiteOptionsLoading(false);
    if (list.length == 0) {
      setErrors((prev) => ({
        ...prev,
        site: "No site available for this region",
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        site: "",
      }));
    }
    setSiteOptions(list);
  };

  const getDivisionOptions = async (regionId, siteId) => {
    setIsDivisionLoading(true);
    try {
      var res = await getDivisionList({
        status: "Y",
        regionId: regionId,
        siteId: siteId,
      });
      if (res.status === "success") {
        const list = res.data.entityList.map((item) => ({
          value: item._ID,
          label: item.NAME,
        }));
        setDivisionOptions(list);
      } else {
        showErrorMessages({ data: res, navigate });
        setDivisionOptions([]);
      }
    } catch (error) {
      console.error("Error fetching divisions:", error);
      showErrorMessages({
        data: { message: "Failed to fetch divisions" },
        navigate,
      });
      setDivisionOptions([]);
    }
    setIsDivisionLoading(false);
  };

  const getSectionOptions = async (divisionId, regionId, siteId) => {
    setIsSectionLoading(true);
    try {
      var res = await getDivisionSectionList({
        classId: divisionId,
        status: "Y",
        regionId: regionId,
        siteId: siteId,
      });
      if (res.status === "success") {
        const list = res.data.entityList.map((item) => ({
          value: item._ID,
          label: item.NAME,
        }));
        setSectionOptions(list);
      } else {
        showErrorMessages({ data: res, navigate });
        setSectionOptions([]);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
      showErrorMessages({
        data: { message: "Failed to fetch sections" },
        navigate,
      });
      setSectionOptions([]);
    }
    setIsSectionLoading(false);
  };

  const getCourseOptions = async (divisionId, sectionId) => {
    setIsCourseLoading(true);
    try {
      var res = await getCoursesList({ status: "Y" });
      if (res.status === "success") {
        const list = res.data.entityList.map((item) => ({
          value: item._ID,
          label: item.NAME,
        }));
        setCourseOptions(list);
      } else {
        showErrorMessages({ data: res, navigate });
        setCourseOptions([]);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      showErrorMessages({
        data: { message: "Failed to fetch courses" },
        navigate,
      });
      setCourseOptions([]);
    }
    setIsCourseLoading(false);
  };

  const getSubjectOptions = async (sectionId) => {
    setIsSubjectLoading(true);
    try {
      var res = await getDivisionSectionSubjectList({
        sectionId,
        classId: formData.academicInfo.division.value,
        status: "Y",
      });
      if (res.status === "success") {
        const list = res.data.entityList.map((item) => ({
          value: item._ID,
          label: item.NAME,
        }));
        setSubjectOptions(list);
      } else {
        showErrorMessages({ data: res, navigate });
        setSubjectOptions([]);
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
      showErrorMessages({
        data: { message: "Failed to fetch subjects" },
        navigate,
      });
      setSubjectOptions([]);
    }
    setIsSubjectLoading(false);
  };

  const validateFields = () => {
    const newErrors = {};

    if (currentStep === 1) {
      const { name, description, academicYear, examType } = formData.examInfo;

      if (!name?.trim()) newErrors.name = "Name is required";
      if (!description?.trim())
        newErrors.description = "Description is required";
      if (!academicYear.value)
        newErrors.academicYear = "Academic Year is required";
      if (!examType.value) newErrors.examType = "Exam Type is required";
    } else if (currentStep === 2) {
      const { region, site, division, section, subject } =
        formData.academicInfo;

      if (!region.value) newErrors.region = "Region is required";
      if (!site.value) newErrors.site = "Site is required";
      if (!division.value) newErrors.division = "Division is required";
      if (!section.value) newErrors.section = "Section is required";
      if (!subject.value) newErrors.subject = "Subject is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getCurrentStepKey = () => {
    return currentStep === 1 ? "examInfo" : "academicInfo";
  };

  const handleNext = async () => {
    if (validateFields()) {
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        await submitForm();
      }
    }
  };

  const submitForm = async () => {
    setLoading(true);

    const dataToSubmit = {
      name: formData.examInfo.name,
      description: formData.examInfo.description,
      academicYearId: formData.examInfo.academicYear.value,
      examType: formData.examInfo.examType.value,
      score: formData.examInfo.score || 0,
      regionId: formData.academicInfo.region.value,
      siteId: formData.academicInfo.site.value,
      classId: formData.academicInfo.division.value,
      sectionId: formData.academicInfo.section.value,
      subjectId: formData.academicInfo.subject.value,
      organizationId:
        getLocalStorageData("profileDetails")["_ORGANIZATION_"]._ID,
      duration: formData.examInfo.duration,
    };

    if (data) {
      dataToSubmit.id = data._ID;
    }

    try {
      const res = data
        ? await updateExaminationTemplate(dataToSubmit, data._ID)
        : await addExaminationTemplate(dataToSubmit);

      if (res.status === "success") {
        callToast(
          "success",
          data ? "Updated" : "Created",
          `Examination Template ${data ? "updated" : "added"} successfully`
        );
        await refresh({ columnRefresh: false, isLoading: false });
        dispatch(closeModal());
      } else {
        const resError = showErrorMessages({
          data: res,
          navigate,
        });

        if (resError != null || resError != undefined) {
          var step = 2;
          var error = {};
          Object.entries(resError).map(([key, value]) => {
            step = Math.min(step, value.step);
            error[key] = value.message;
          });
          setErrors(error);
          setCurrentStep(step);
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      callToast("error", "Error", "Failed to submit examination template");
    }

    setLoading(false);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  useEffect(() => {
    getAcademicYearOptions();
    getRegionList();

    if (modal.isEditing) {
      getExamDetailsData();
    }
  }, []);

  if (loadingData) {
    return (
      <center>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </center>
    );
  }

  return (
    <section className="login-content">
      <div className="m-5 mt-1">
        <div className="d-flex">
          <div className="w-100">
            <div className="stepper mb-3">
              <div className="d-flex justify-content-between">
                <div className={`step ${currentStep === 1 ? "active" : ""}`}>
                  Exam Info
                </div>
                <div className={`step ${currentStep === 2 ? "active" : ""}`}>
                  Academic Info
                </div>
              </div>
            </div>

            <form>
              {currentStep === 1 ? (
                <ExamInfoStep
                  formData={formData.examInfo}
                  errors={errors}
                  academicYearOptions={academicYearOptions}
                  examTypeOptions={examTypeOptions}
                  isAcademicYearLoading={isAcademicYearLoading}
                  onChange={handleChange}
                />
              ) : (
                <AcademicInfoStep
                  formData={formData.academicInfo}
                  errors={errors}
                  regionOptions={regionOptions}
                  siteOptions={siteOptions}
                  divisionOptions={divisionOptions}
                  sectionOptions={sectionOptions}
                  courseOptions={courseOptions}
                  subjectOptions={subjectOptions}
                  isSiteOptionsLoading={isSiteOptionsLoading}
                  isDivisionLoading={isDivisionLoading}
                  isSectionLoading={isSectionLoading}
                  isCourseLoading={isCourseLoading}
                  isSubjectLoading={isSubjectLoading}
                  isSiteSearchable={isSiteSearchable}
                  isRegionSearchable={isRegionSearchable}
                  onChange={handleChange}
                  getSiteList={getSiteList}
                />
              )}

              <div className="d-flex justify-content-end mt-4 gap-2">
                {currentStep > 1 && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleBack}
                    disabled={loading}
                  >
                    Back
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleNext}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="spinner-border text-light" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  ) : currentStep < 2 ? (
                    "Next"
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Step Components
const ExamInfoStep = ({
  formData,
  errors,
  academicYearOptions,
  examTypeOptions,
  isAcademicYearLoading,
  onChange,
}) => (
  <>
    <div className="mb-3">
      <label className="form-label">Name</label>
      <input
        type="text"
        className="form-control"
        placeholder="Enter Exam Name"
        value={formData.name}
        onChange={(e) => onChange("examInfo", "name", e.target.value)}
      />
      {errors.name && <small className="text-danger">{errors.name}</small>}
    </div>
    <div className="mb-3">
      <label className="form-label">Description</label>
      <textarea
        className="form-control"
        placeholder="Enter Exam Description"
        rows="1"
        value={formData.description}
        onChange={(e) => onChange("examInfo", "description", e.target.value)}
      />
      {errors.description && (
        <small className="text-danger">{errors.description}</small>
      )}
    </div>

    <div className="row mb-3">
      <div className="col-md-6">
        <label className="form-label">Academic Year</label>
        <Select
          options={academicYearOptions}
          value={formData.academicYear.label ? formData.academicYear : null}
          styles={selectStyle}
          onChange={(newValue) =>
            onChange(
              "examInfo",
              "academicYear",
              newValue || { value: "", label: "" }
            )
          }
          components={isAcademicYearLoading ? { DropdownIndicator } : null}
          isSearchable={!isAcademicYearLoading}
          placeholder="Select Academic Year"
          className="basic-single"
          classNamePrefix="select"
        />
        {errors.academicYear && (
          <small className="text-danger">{errors.academicYear}</small>
        )}
      </div>

      <div className="col-md-6">
        <label className="form-label">Exam Type</label>
        <Select
          options={examTypeOptions}
          value={formData.examType.label ? formData.examType : null}
          styles={selectStyle}
          onChange={(newValue) =>
            onChange(
              "examInfo",
              "examType",
              newValue || { value: "", label: "" }
            )
          }
          placeholder="Select Exam Type"
          className="basic-single"
          classNamePrefix="select"
        />
        {errors.examType && (
          <small className="text-danger">{errors.examType}</small>
        )}
      </div>
    </div>

    <div className="row mb-3 ">
      <div className="col-md-6">
        <div className="mb-3">
          <label className="form-label no-asterisk">Score (Optional)</label>
          <input
            type="number"
            className="form-control"
            placeholder="Enter Score"
            value={formData.score}
            onChange={(e) => onChange("examInfo", "score", e.target.value)}
          />
        </div>
      </div>
      <div className="col-md-6">
        <div className="mb-3">
          <label className="form-label no-asterisk">Duration </label>
          <CustomTimePicker
            value={formData.duration}
            onChange={(timeValue) =>
              onChange("examInfo", "duration", timeValue)
            }
            placeholder="Select Duration"
            className="custom-time-picker"
          />
        </div>
      </div>
    </div>
  </>
);

const AcademicInfoStep = ({
  formData,
  errors,
  regionOptions,
  siteOptions,
  divisionOptions,
  sectionOptions,
  courseOptions,
  subjectOptions,
  isSiteOptionsLoading,
  isDivisionLoading,
  isSectionLoading,
  isCourseLoading,
  isSubjectLoading,
  isSiteSearchable,
  isRegionSearchable,
  onChange,
  getSiteList,
}) => (
  <>
    <div className="mb-3">
      <label className="form-label">Region</label>
      <Select
        options={regionOptions}
        value={formData.region.label ? formData.region : null}
        styles={selectStyle}
        onChange={(newValue) =>
          onChange(
            "academicInfo",
            "region",
            newValue || { value: "", label: "" }
          )
        }
        noOptionsMessage={() => {
          return !isRegionSearchable
            ? "Region Access not available"
            : "No matches found";
        }}
        isSearchable={isRegionSearchable}
        placeholder="Select Region"
        className="basic-single"
        classNamePrefix="select"
      />
      {errors.region && <small className="text-danger">{errors.region}</small>}
    </div>

    <div className="mb-3">
      <label className="form-label">Site</label>
      <Select
        options={siteOptions}
        value={formData.site.label ? formData.site : null}
        styles={selectStyle}
        onChange={(newValue) =>
          onChange("academicInfo", "site", newValue || { value: "", label: "" })
        }
        onInputChange={(inputValue, { action }) => {
          if (action === "input-change") {
            getSiteList(formData.region.value, inputValue);
          }
        }}
        components={isSiteOptionsLoading ? { DropdownIndicator } : null}
        noOptionsMessage={() => {
          return !isSiteSearchable
            ? "Site Access Not available"
            : "No matches found";
        }}
        isSearchable={isSiteSearchable}
        placeholder="Select Site"
        className="basic-single"
        classNamePrefix="select"
        isDisabled={!formData.region.value}
      />
      {errors.site && <small className="text-danger">{errors.site}</small>}
    </div>

    <div className="mb-3">
      <label className="form-label">Division</label>
      <Select
        options={divisionOptions}
        value={formData.division.label ? formData.division : null}
        styles={selectStyle}
        onChange={(newValue) =>
          onChange(
            "academicInfo",
            "division",
            newValue || { value: "", label: "" }
          )
        }
        components={isDivisionLoading ? { DropdownIndicator } : null}
        isSearchable={!isDivisionLoading}
        placeholder="Select Division"
        className="basic-single"
        classNamePrefix="select"
        isDisabled={!formData.site.value}
      />
      {errors.division && (
        <small className="text-danger">{errors.division}</small>
      )}
    </div>

    <div className="mb-3">
      <label className="form-label">Section</label>
      <Select
        options={sectionOptions}
        value={formData.section.label ? formData.section : null}
        styles={selectStyle}
        onChange={(newValue) =>
          onChange(
            "academicInfo",
            "section",
            newValue || { value: "", label: "" }
          )
        }
        components={isSectionLoading ? { DropdownIndicator } : null}
        isSearchable={!isSectionLoading}
        placeholder="Select Section"
        className="basic-single"
        classNamePrefix="select"
        isDisabled={!formData.division.value}
      />
      {errors.section && (
        <small className="text-danger">{errors.section}</small>
      )}
    </div>

    <div className="mb-3">
      <label className="form-label">Subject</label>
      <Select
        options={subjectOptions}
        value={formData.subject.label ? formData.subject : null}
        styles={selectStyle}
        onChange={(newValue) =>
          onChange(
            "academicInfo",
            "subject",
            newValue || { value: "", label: "" }
          )
        }
        components={isSubjectLoading ? { DropdownIndicator } : null}
        isSearchable={!isSubjectLoading}
        placeholder="Select Subject"
        className="basic-single"
        classNamePrefix="select"
        isDisabled={!formData.section.value}
      />
      {errors.subject && (
        <small className="text-danger">{errors.subject}</small>
      )}
    </div>
  </>
);

const DropdownIndicator = (props) => {
  return (
    <div {...props.innerProps}>
      <div
        className="spinner-border text-primary spinner-border-sm m-2"
        role="status"
      >
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );
};

export default AddExaminationTemplateForm;
